import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRightIcon,
  CheckIcon,
  SparklesIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

export const AnimatedSoftwareCard = ({ 
  name, 
  tagline, 
  description, 
  features, 
  price, 
  link, 
  cta, 
  icon, 
  gradient, 
  accentColor,
  isPopular = false 
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`relative group cursor-pointer transition-all duration-500 transform ${
        isHovered ? 'scale-105' : 'scale-100'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card Container */}
      <div className={`relative overflow-hidden rounded-2xl shadow-xl transition-all duration-500 ${
        isHovered ? 'shadow-2xl' : 'shadow-lg'
      }`}>
        {/* Background Gradient */}
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient} transition-all duration-500 ${
          isHovered ? 'opacity-100' : 'opacity-90'
        }`} />
        
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className={`absolute inset-0 transition-all duration-1000 ${
            isHovered ? 'animate-pulse' : ''
          }`}>
            <div className="absolute top-0 left-0 w-full h-full bg-white rounded-full transform scale-0 group-hover:scale-150 transition-transform duration-1000" />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 p-8 text-white">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl bg-white bg-opacity-20 transition-all duration-300 ${
                isHovered ? 'bg-opacity-30 scale-110' : ''
              }`}>
                {icon}
              </div>
              {isPopular && (
                <div className={`px-3 py-1 rounded-full bg-yellow-400 text-yellow-900 text-sm font-medium transition-all duration-300 ${
                  isHovered ? 'scale-110' : ''
                }`}>
                  <SparklesIcon className="h-4 w-4 inline mr-1" />
                  Popular
                </div>
              )}
            </div>
            
            <h3 className={`text-2xl font-bold mb-2 transition-all duration-300 ${
              isHovered ? 'text-3xl' : ''
            }`}>
              {name}
            </h3>
            
            <p className={`text-lg font-semibold mb-3 transition-all duration-300 ${
              isHovered ? 'text-xl' : ''
            }`}>
              {tagline}
            </p>
            
            <p className="text-white/80 text-sm leading-relaxed">
              {description}
            </p>
          </div>

          {/* Features */}
          <div className="mb-6">
            <ul className="space-y-2">
              {features.map((feature, index) => (
                <li 
                  key={index} 
                  className={`flex items-center transition-all duration-300 ${
                    isHovered ? 'transform translate-x-2' : ''
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <CheckIcon className="h-4 w-4 text-green-300 mr-3 flex-shrink-0" />
                  <span className="text-white/90 text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Price and CTA */}
          <div className="flex items-center justify-between">
            <div className={`text-2xl font-bold transition-all duration-300 ${
              isHovered ? 'text-3xl' : ''
            }`}>
              {price}
            </div>
            
            <Link
              to={link}
              className={`flex items-center px-6 py-3 bg-white text-gray-900 rounded-lg font-medium transition-all duration-300 hover:bg-gray-100 ${
                isHovered ? 'scale-110 shadow-lg' : ''
              }`}
            >
              {cta}
              <ArrowRightIcon className={`ml-2 h-4 w-4 transition-all duration-300 ${
                isHovered ? 'transform translate-x-1' : ''
              }`} />
            </Link>
          </div>
        </div>

        {/* Hover Overlay Effect */}
        <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 transition-all duration-500 ${
          isHovered ? 'opacity-10 animate-pulse' : ''
        }`} />

        {/* Floating Elements */}
        <div className="absolute top-4 right-4 opacity-0 transition-all duration-500 group-hover:opacity-100">
          <BoltIcon className="h-6 w-6 text-yellow-300 animate-bounce" />
        </div>
        
        <div className="absolute bottom-4 left-4 opacity-0 transition-all duration-500 group-hover:opacity-100">
          <SparklesIcon className="h-5 w-5 text-white animate-pulse" />
        </div>
      </div>

      {/* Glow Effect */}
      <div className={`absolute -inset-1 bg-gradient-to-r ${gradient} rounded-2xl blur opacity-0 transition-all duration-500 ${
        isHovered ? 'opacity-30' : ''
      }`} />
    </div>
  );
};
